import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import './SocialPrepubsManager.css';

const TIMEZONES = [
  'America/Tijuana',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Hermosillo',
  'America/Chihuahua',
  'America/Mazatlan',
  'America/Cancun',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
];

function emptyRule() {
  return {
    name: '',
    facebook_group_url: '',
    facebook_group_name: '',
    headline_city: '',
    entry_page_id: '',
    entry_page_slug: '',
    entry_page_full_slug: '',
    ports: [],
    port_labels: [],
    timezone: 'America/Tijuana',
    site_url: 'https://bordergarita.com',
    active: true,
  };
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

function openFacebookGroup(openUrls, groupUrl) {
  const web = openUrls?.web || groupUrl;
  if (!web) return;
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (!isIOS && !isAndroid) {
    window.open(web, '_blank', 'noopener,noreferrer');
    return;
  }
  const appUrl = isIOS ? openUrls?.ios || web : openUrls?.android || web;
  const fallback = openUrls?.mobile || web;
  const started = Date.now();
  window.location.href = appUrl;
  window.setTimeout(() => {
    if (document.visibilityState === 'visible' && Date.now() - started < 2000) {
      window.location.href = fallback;
    }
  }, 900);
}

export default function SocialPrepubsManager() {
  const toast = useRef(null);
  const [tab, setTab] = useState('generate');
  const [rules, setRules] = useState([]);
  const [entryPages, setEntryPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [previews, setPreviews] = useState({});
  const [openingGroup, setOpeningGroup] = useState(false);
  const previewRefs = useRef({});
  const textRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const [rulesRes, pagesRes] = await Promise.all([
        api.get('/social_prepubs'),
        api.get('/social_prepubs/entry-pages'),
      ]);
      setRules(rulesRes.data || []);
      setEntryPages(pagesRes.data || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load FB templates',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeRules = useMemo(() => rules.filter((r) => r.active !== false), [rules]);
  const selectedPage = editing
    ? entryPages.find((p) => String(p._id) === String(editing.entry_page_id))
    : null;

  function setField(key, value) {
    setEditing((prev) => ({ ...prev, [key]: value }));
  }

  function selectEntryPage(pageId) {
    const page = entryPages.find((p) => String(p._id) === String(pageId));
    setEditing((prev) => ({
      ...prev,
      entry_page_id: pageId,
      entry_page_slug: page?.slug || '',
      entry_page_full_slug: page?.full_slug || '',
      headline_city: prev.headline_city || page?.city || '',
      ports: [],
      port_labels: [],
    }));
  }

  function togglePort(port) {
    setEditing((prev) => {
      const ports = prev.ports || [];
      const labels = prev.port_labels || [];
      if (ports.includes(port.number)) {
        return {
          ...prev,
          ports: ports.filter((n) => n !== port.number),
          port_labels: labels.filter((l) => l.number !== port.number),
        };
      }
      return {
        ...prev,
        ports: [...ports, port.number],
        port_labels: [...labels, { number: port.number, label: port.label }],
      };
    });
  }

  async function saveRule() {
    if (!editing?.facebook_group_url) {
      toast.current?.show({ severity: 'warn', summary: 'Facebook group URL is required' });
      return;
    }
    if (!editing.entry_page_id || !editing.ports?.length) {
      toast.current?.show({ severity: 'warn', summary: 'Select an entry page and ports' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...editing,
        name:
          editing.name ||
          `${editing.headline_city || editing.entry_page_slug} · ${editing.facebook_group_name || 'Facebook'}`,
      };
      if (editing._id) await api.put(`/social_prepubs/${editing._id}`, payload);
      else await api.post('/social_prepubs', payload);
      toast.current?.show({ severity: 'success', summary: 'Template saved', life: 1800 });
      setEditing(null);
      await load();
      setTab('generate');
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not save',
        detail: err.response?.data?.error || err.message,
      });
    } finally {
      setSaving(false);
    }
  }

  async function deleteRule(id) {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/social_prepubs/${id}`);
      if (previews[id]) {
        setPreviews((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
      await load();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Could not delete' });
    }
  }

  async function generate(rule) {
    try {
      setGeneratingId(rule._id);
      const { data } = await api.post(`/social_prepubs/${rule._id}/generate`);
      setPreviews((prev) => ({
        ...prev,
        [rule._id]: {
          ruleId: rule._id,
          ruleName: rule.name || rule.facebook_group_name,
          ...data,
          text: data.text || '',
        },
      }));
      toast.current?.show({
        severity: 'success',
        summary: data.used_gemini ? 'Copy ready (Gemini)' : 'Copy ready',
        life: 1800,
      });
      window.setTimeout(() => {
        previewRefs.current[rule._id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        textRefs.current[rule._id]?.focus({ preventScroll: true });
      }, 80);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not generate',
        detail: err.response?.data?.error || err.message,
        life: 4000,
      });
    } finally {
      setGeneratingId(null);
    }
  }

  async function handleCopy(preview, openGroup = false) {
    if (!preview?.text || openingGroup) return;
    const ok = await copyText(preview.text);
    if (!ok) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not copy',
        life: 1800,
      });
      return;
    }

    if (!openGroup) {
      toast.current?.show({
        severity: 'success',
        summary: 'Copied',
        detail: 'Paste it in the Facebook group',
        life: 1800,
      });
      return;
    }

    setOpeningGroup(true);
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    openFacebookGroup(preview.open_urls, preview.facebook_group_url);
    setOpeningGroup(false);
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: 'Opening group… paste the text',
      life: 2200,
    });
  }

  if (loading) {
    return (
      <div className="social-prepub-page">
        <div className="social-prepub-loading">
          <i className="pi pi-spin pi-spinner" />
          <span>Loading FB templates…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="social-prepub-page">
      <Toast ref={toast} position="top-center" />

      <header className="social-prepub-header">
        <p className="social-prepub-kicker">Facebook</p>
        <h1>FB templates</h1>
        <p className="social-prepub-lead">
          Generate wait-time copy, copy it, then open the group to paste.
        </p>
      </header>

      <div className="social-prepub-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'generate'}
          className={tab === 'generate' ? 'is-active' : ''}
          onClick={() => setTab('generate')}
        >
          Generate
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rules'}
          className={tab === 'rules' ? 'is-active' : ''}
          onClick={() => setTab('rules')}
        >
          Templates
        </button>
      </div>

      {tab === 'generate' && (
        <section className="social-prepub-generate">
          {activeRules.length === 0 && (
            <div className="social-prepub-empty">
              <p>No active templates yet.</p>
              <Button
                label="Create template"
                icon="pi pi-plus"
                onClick={() => {
                  setEditing(emptyRule());
                  setTab('rules');
                }}
              />
            </div>
          )}

          {activeRules.map((rule) => {
            const preview = previews[rule._id];
            return (
              <article key={rule._id} className="social-prepub-card">
                <div className="social-prepub-card-top">
                  <strong>{rule.name || rule.facebook_group_name || 'FB group'}</strong>
                  <span>
                    {rule.headline_city || rule.entry_page_slug}
                    {rule.port_labels?.length
                      ? ` · ${rule.port_labels.map((p) => p.label).join(', ')}`
                      : ''}
                  </span>
                </div>
                <Button
                  label={generatingId === rule._id ? 'Generating…' : 'Generate post'}
                  icon={generatingId === rule._id ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'}
                  className="social-prepub-generate-btn"
                  disabled={!!generatingId || openingGroup}
                  onClick={() => generate(rule)}
                />
                {preview && (
                  <div
                    ref={(el) => {
                      previewRefs.current[rule._id] = el;
                    }}
                    className="social-prepub-preview"
                  >
                    <div className="social-prepub-preview-head">
                      <strong>Generated post</strong>
                      <small>
                        {preview.used_gemini ? 'Paragraph with Gemini' : 'Fallback paragraph'}
                      </small>
                    </div>
                    <div className="social-prepub-actions">
                      <Button
                        label="Copy"
                        icon="pi pi-copy"
                        className="p-button-outlined"
                        disabled={openingGroup}
                        onClick={() => handleCopy(preview, false)}
                      />
                      <Button
                        label={openingGroup ? 'Opening group…' : 'Copy & open group'}
                        icon={openingGroup ? 'pi pi-spin pi-spinner' : 'pi pi-external-link'}
                        disabled={openingGroup}
                        onClick={() => handleCopy(preview, true)}
                      />
                    </div>
                    <textarea
                      ref={(el) => {
                        textRefs.current[rule._id] = el;
                      }}
                      className="social-prepub-textarea"
                      value={preview.text}
                      onChange={(e) =>
                        setPreviews((prev) => ({
                          ...prev,
                          [rule._id]: { ...prev[rule._id], text: e.target.value },
                        }))
                      }
                      rows={14}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}

      {openingGroup && (
        <div className="social-prepub-open-overlay" role="status" aria-live="polite">
          <div className="social-prepub-open-card">
            <div className="social-prepub-open-spinner" />
            <strong>Copied</strong>
            <span>Opening Facebook group…</span>
          </div>
        </div>
      )}

      {tab === 'rules' && (
        <section className="social-prepub-rules">
          {!editing && (
            <>
              <div className="social-prepub-rules-toolbar">
                <Button
                  label="New template"
                  icon="pi pi-plus"
                  className="social-prepub-new-rule-btn"
                  onClick={() => setEditing(emptyRule())}
                />
              </div>
              {rules.map((rule) => (
                <article key={rule._id} className="social-prepub-card">
                  <div className="social-prepub-card-top">
                    <strong>{rule.name || rule.facebook_group_name || 'Untitled'}</strong>
                    <span>{rule.facebook_group_url}</span>
                    <span>
                      {(rule.port_labels || []).map((p) => p.label).join(', ') || 'No ports'}
                    </span>
                  </div>
                  <div className="social-prepub-card-actions">
                    <Button
                      label="Edit"
                      icon="pi pi-pencil"
                      className="p-button-outlined p-button-sm"
                      onClick={() =>
                        setEditing({
                          ...emptyRule(),
                          ...rule,
                          entry_page_id: String(rule.entry_page_id || ''),
                          ports: (rule.ports || []).map(String),
                        })
                      }
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-outlined p-button-danger p-button-sm"
                      onClick={() => deleteRule(rule._id)}
                    />
                  </div>
                </article>
              ))}
            </>
          )}

          {editing && (
            <div className="social-prepub-form">
              <label>
                Name
                <InputText
                  value={editing.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Tijuana · Wait times group"
                />
              </label>
              <label>
                Facebook group URL
                <InputText
                  value={editing.facebook_group_url}
                  onChange={(e) => setField('facebook_group_url', e.target.value)}
                  placeholder="https://www.facebook.com/groups/..."
                />
              </label>
              <label>
                Group name
                <InputText
                  value={editing.facebook_group_name}
                  onChange={(e) => setField('facebook_group_name', e.target.value)}
                  placeholder="Tijuana wait times"
                />
              </label>
              <label>
                City in headline
                <InputText
                  value={editing.headline_city}
                  onChange={(e) => setField('headline_city', e.target.value)}
                  placeholder="Tijuana"
                />
              </label>
              <label>
                Entry page
                <Dropdown
                  value={editing.entry_page_id}
                  options={entryPages.map((p) => ({
                    label: p.title || p.slug,
                    value: String(p._id),
                    slug: p.full_slug || p.slug,
                  }))}
                  filterInputAutoFocus
                  onChange={(e) => selectEntryPage(e.value)}
                  placeholder="Select"
                  filter
                  className="w-full"
                  itemTemplate={(option) => (
                    <div>
                      <div className="font-bold">{option.label}</div>
                      <div className="text-sm" style={{ color: '#888' }}>
                        {option.slug}
                      </div>
                    </div>
                  )}
                />
              </label>

              {selectedPage?.groups?.length > 0 && (
                <div className="social-prepub-ports">
                  <span>Ports</span>
                  {selectedPage.groups.map((group) => (
                    <div key={group.crossing_name} className="social-prepub-port-group">
                      <strong>{group.crossing_name}</strong>
                      {group.ports.map((port) => (
                        <label key={port.number} className="social-prepub-port">
                          <Checkbox
                            checked={(editing.ports || []).includes(port.number)}
                            onChange={() =>
                              togglePort({
                                ...port,
                                label: group.crossing_name || port.label,
                              })
                            }
                          />
                          {group.crossing_name || port.label} ({port.number})
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <label>
                Timezone
                <Dropdown
                  value={editing.timezone}
                  options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
                  onChange={(e) => setField('timezone', e.value)}
                  filterInputAutoFocus
                  className="w-full"
                  filter
                />
              </label>
              <label className="social-prepub-switch">
                <InputSwitch
                  checked={editing.active !== false}
                  onChange={(e) => setField('active', e.value)}
                />
                Active
              </label>
              <div className="social-prepub-form-actions">
                <Button
                  label="Cancel"
                  className="p-button-outlined"
                  onClick={() => setEditing(null)}
                />
                <Button label="Save" loading={saving} onClick={saveRule} />
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
